import { categoryLabel } from "@listou/shared/categories";
import { isProductIndexable } from "@listou/shared/constants";
import type { PublicProductResponse } from "@listou/shared/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";

import { Sticker } from "../components/list-page/parts";
import { ProductCard } from "../components/product-card";
import { ReviewsSection } from "../components/reviews-section";
import { SiteFooter, SiteHeader } from "../components/site-header";
import { formatRating, Stars } from "../components/stars";
import { useToast } from "../components/toast";
import { Dialog as Modal } from "../components/ui/dialog";
import { PageLoader, Spinner } from "../components/ui/spinner";
import { ApiError, wishesApi } from "../lib/api";
import { formatPrice, plural } from "../lib/format";
import { seo } from "../lib/head";
import { listsQuery, productQuery, wishesQuery } from "../lib/queries";
import { errorMessage, viewerQuery } from "../lib/session";
import { FALLBACK_STICKER } from "../lib/theme";

export const Route = createFileRoute("/p/$slug")({
  staticData: { ssr: true },
  loader: async ({ context: { queryClient }, params: { slug } }) => {
    try {
      return await queryClient.query(productQuery(slug));
    } catch (error) {
      // A 404 status, not a page saying "not found" with a 200: search engines would
      // otherwise keep a removed product indexed.
      if (error instanceof ApiError && error.status === 404) throw notFound();
      // Anything else stays in the query cache, and the page shows its own error.
      return null;
    }
  },
  head: ({ loaderData }) => (loaderData ? productSeo(loaderData) : {}),
  component: ProductPage,
});

const productSeo = ({ product, reviews }: PublicProductResponse) => {
  const { title, description, rating } = product;
  const price = formatPrice(product.priceCents, product.currency);
  // The price and the parents' verdict lead: they are what a searcher compares in the
  // results, and the merchant's own text says nothing the shop's listing does not.
  const lead = price ? `${title} : ${price} chez ${product.merchant}.` : `${title}.`;
  const verdict = rating
    ? `Noté ${formatRating(rating.average)}/5 par ${plural(rating.count, "parent", "parents")} sur Listou.`
    : product.wishCount > 0
      ? `Sur ${plural(product.wishCount, "liste d'envies", "listes d'envies")} de parents.`
      : "";
  return seo({
    title: `${title} · Listou`,
    description: `${lead} ${verdict} Ajoutez-le à votre liste, vos proches le réservent.`
      .replace(/\s+/g, " ")
      .trim(),
    // The drawn card, not the raw product shot: its size is unknown and it carries no stars.
    image: product.shareImageUrl,
    noindex: !isProductIndexable(product),
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: title,
      ...(product.brand ? { brand: { "@type": "Brand", name: product.brand } } : {}),
      ...(product.imageUrl ? { image: product.imageUrl } : {}),
      ...(description ? { description } : {}),
      ...(product.priceCents !== null
        ? {
            offers: {
              "@type": "Offer",
              price: (product.priceCents / 100).toFixed(2),
              priceCurrency: product.currency,
              seller: { "@type": "Organization", name: product.merchant },
            },
          }
        : {}),
      // Only the reviews shown on the page: the stars Google may display must be the ones
      // a visitor can read.
      ...(reviews.summary.average !== null
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: reviews.summary.average,
              reviewCount: reviews.summary.count,
              bestRating: 5,
              worstRating: 1,
            },
            review: reviews.items.slice(0, 5).map((review) => ({
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: review.rating, bestRating: 5 },
              author: { "@type": "Person", name: review.authorName },
              datePublished: review.createdAt.slice(0, 10),
              // Undefined drops out of the JSON: no empty reviewBody for a stars-only review.
              reviewBody: review.body ?? undefined,
            })),
          }
        : {}),
    },
  });
};

function ProductPage() {
  const { slug } = Route.useParams();
  const viewer = useQuery(viewerQuery);
  const product = useQuery(productQuery(slug));
  const [choosing, setChoosing] = useState(false);

  const signedIn = viewer.isSuccess;

  return (
    <div className="min-h-dvh">
      <SiteHeader signedIn={signedIn} />
      <main className="mx-auto grid max-w-6xl gap-14 px-4 pt-4 pb-20 sm:px-6">
        {product.isPending && <PageLoader />}
        {product.isError && (
          <div className="grid justify-items-start gap-4 py-12">
            <h1 className="text-4xl font-semibold">
              {product.error instanceof ApiError && product.error.status === 404
                ? "Ce produit n'a pas de page"
                : "Le produit n'a pas pu être chargé"}
            </h1>
            <Link to="/idees-cadeaux" className="btn">
              Voir les idées cadeaux
            </Link>
          </div>
        )}

        {product.data && (
          <>
            {product.data.product.category && product.data.product.occasions.includes("birth") && (
              <nav aria-label="Fil d'Ariane" className="-mb-8 text-muted">
                <Link to="/idees-cadeaux" className="font-bold hover:underline">
                  Idées cadeaux
                </Link>
                <span aria-hidden> › </span>
                <Link to="/idees-cadeaux-naissance" className="font-bold hover:underline">
                  Naissance
                </Link>
                <span aria-hidden> › </span>
                <Link
                  to="/idees-cadeaux-naissance/$category"
                  params={{ category: product.data.product.category }}
                  className="font-bold hover:underline"
                >
                  {categoryLabel(product.data.product.category)}
                </Link>
              </nav>
            )}
            <article className="grid gap-8 md:grid-cols-2 md:items-start">
              <div className="relative grid aspect-square place-items-center rounded-[2rem] border-[1.5px] border-line bg-white md:sticky md:top-6">
                {product.data.product.imageUrl ? (
                  <img
                    src={product.data.product.imageUrl}
                    alt=""
                    className="absolute inset-0 size-full object-contain p-8"
                  />
                ) : (
                  <Sticker src={FALLBACK_STICKER} className="w-1/2 object-contain" />
                )}
              </div>
              <div className="grid content-start gap-5">
                {product.data.product.brand && (
                  <p className="text-lg font-bold text-muted">{product.data.product.brand}</p>
                )}
                {/* Shop titles are keyword lists ("Lot de 12 Portions…, Congélation, Lait Maternel");
                    the clamp hides the tail visually, the full text stays for search engines. */}
                <h1
                  className="line-clamp-2 text-3xl font-semibold text-balance sm:text-4xl"
                  title={product.data.product.title}
                >
                  {product.data.product.title}
                </h1>
                {product.data.product.rating && (
                  <a
                    href="#avis"
                    className="flex w-fit items-center gap-2 rounded-lg hover:underline"
                  >
                    <Stars value={product.data.product.rating.average} />
                    <span className="font-bold">
                      {formatRating(product.data.product.rating.average)}
                    </span>
                    <span className="text-muted">
                      ({plural(product.data.product.rating.count, "avis", "avis")})
                    </span>
                  </a>
                )}
                {product.data.product.priceCents !== null && (
                  <p className="text-2xl">
                    Environ{" "}
                    <strong>
                      {formatPrice(product.data.product.priceCents, product.data.product.currency)}
                    </strong>
                    <span className="ml-2 text-base text-muted">prix indicatif</span>
                  </p>
                )}
                {product.data.product.wishCount > 0 && (
                  <p className="w-fit rounded-2xl bg-butter/50 px-4 py-3 text-lg font-bold">
                    {plural(product.data.product.wishCount, "parent le veut", "parents le veulent")}
                    {product.data.product.wishCount7d > 0 &&
                      `, dont ${product.data.product.wishCount7d} cette semaine`}
                  </p>
                )}
                {product.data.product.description && (
                  <Description text={product.data.product.description} />
                )}
                <div className="flex flex-wrap gap-3">
                  {signedIn ? (
                    <button
                      type="button"
                      className="btn min-h-13 px-7 text-lg"
                      onClick={() => setChoosing(true)}
                    >
                      Ajouter à ma liste
                    </button>
                  ) : (
                    <Link
                      to="/login"
                      search={{ redirectTo: `/p/${slug}` }}
                      className="btn min-h-13 px-7 text-lg"
                    >
                      Ajouter à ma liste
                    </Link>
                  )}
                  <a
                    className="btn-secondary min-h-13 px-7 text-lg"
                    href={product.data.product.goUrl}
                    target="_blank"
                    rel="sponsored nofollow noopener"
                  >
                    Voir sur {product.data.product.merchant}
                  </a>
                </div>
                <p className="text-sm text-muted">
                  Lien affilié : Listou peut toucher une commission, sans surcoût pour vous.
                </p>
              </div>
            </article>

            <ReviewsSection slug={slug} data={product.data} signedIn={signedIn} />

            {product.data.related.length > 0 && (
              <section className="grid gap-5">
                <h2 className="text-3xl font-semibold">Souvent sur les mêmes listes</h2>
                <ul className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {product.data.related.map((item) => (
                    <li key={item.id}>
                      <ProductCard product={item} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {signedIn && (
              <ChooseListDialog
                open={choosing}
                onClose={() => setChoosing(false)}
                product={product.data.product}
              />
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

/** Past this length the text is clamped behind "Lire la suite": shop copy runs to 1000 characters. */
const DESCRIPTION_PREVIEW = 240;

function Description({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > DESCRIPTION_PREVIEW;
  return (
    <div className="grid max-w-[60ch] justify-items-start gap-1">
      {/* Amazon descriptions are bullet points, one per line. */}
      <p className={`whitespace-pre-line text-ink/80 ${long && !open ? "line-clamp-4" : ""}`}>
        {text}
      </p>
      {long && (
        <button
          type="button"
          className="font-bold underline underline-offset-2 hover:text-ink/70"
          onClick={() => setOpen(!open)}
        >
          {open ? "Réduire" : "Lire la suite"}
        </button>
      )}
    </div>
  );
}

function ChooseListDialog({
  open,
  onClose,
  product,
}: {
  open: boolean;
  onClose: () => void;
  product: { id: string; title: string; priceCents: number | null };
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const lists = useQuery({ ...listsQuery, enabled: open });

  const add = useMutation({
    mutationFn: (listId: string) =>
      wishesApi.create(listId, {
        title: product.title,
        priceCents: product.priceCents,
        productId: product.id,
      }),
    onSuccess: (_data, listId) => {
      void queryClient.invalidateQueries({ queryKey: wishesQuery(listId).queryKey });
      void queryClient.invalidateQueries({ queryKey: listsQuery.queryKey });
      toast("Ajouté à votre liste");
      onClose();
    },
  });

  const active = lists.data?.lists.filter((list) => !list.archived) ?? [];

  return (
    <Modal open={open} onClose={onClose} title="Dans quelle liste ?">
      {lists.isPending && <Spinner />}
      {lists.isSuccess && active.length === 0 && (
        <div className="grid justify-items-start gap-3">
          <p>Vous n'avez pas encore de liste.</p>
          <Link to="/nouvelle-liste" className="btn">
            Créer ma liste
          </Link>
        </div>
      )}
      <ul className="grid gap-2">
        {active.map((list) => (
          <li key={list.id}>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-2xl border-[1.5px] border-line p-3 text-left hover:border-ink/30"
              onClick={() => add.mutate(list.id)}
              disabled={add.isPending}
            >
              <Sticker
                src={list.appearance.stickers?.[0]?.url ?? FALLBACK_STICKER}
                className="size-10 object-contain"
              />
              <span className="font-bold">{list.title}</span>
            </button>
          </li>
        ))}
      </ul>
      {add.isError && <p className="text-danger">{errorMessage(add.error, "L'ajout a échoué.")}</p>}
    </Modal>
  );
}
